import React, { useEffect, useState } from "react";
import axios from "axios";
import { ShieldAlert, Loader2, Send, Trash2, MessageSquare, Plus, Pencil, Share2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const client = axios.create({ baseURL: API, timeout: 30000, withCredentials: true });

const SEVERITY = {
  high: "border-red-500/60 text-red-300",
  medium: "border-amber-500/60 text-amber-300",
  low: "border-sky-500/60 text-sky-300",
  info: "border-zinc-600 text-zinc-300",
};
const STATUS = {
  open: "bg-red-500/15 text-red-300",
  fixing: "bg-amber-500/15 text-amber-300",
  fixed: "bg-emerald-500/15 text-emerald-300",
  monitoring: "bg-sky-500/15 text-sky-300",
};
const STATUSES = Object.keys(STATUS);

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : "Something went wrong.";
};

function Picker({ value, onChange, options, testId, className = "" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={`h-8 text-[10px] uppercase tracking-[0.2em] bg-black border-zinc-800 rounded-none ${className}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o} data-testid={`${testId}-option-${o}`} className="text-xs uppercase tracking-[0.2em]">{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Replies({ adv, onChange }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const id = adv.advisory_id;

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/security/advisories/${id}/replies`, { body });
      onChange({ ...adv, replies: [...(adv.replies || []), data.reply] });
      setBody("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rid) => {
    try {
      await client.delete(`/security/advisories/${id}/replies/${rid}`);
      onChange({ ...adv, replies: adv.replies.filter((r) => r.reply_id !== rid) });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mt-4 border-t border-zinc-800 pt-3 space-y-2" data-testid={`advisory-replies-${id}`}>
      {(adv.replies || []).map((r) => (
        <div key={r.reply_id} className="text-xs flex gap-2 items-start" data-testid={`advisory-reply-${r.reply_id}`}>
          <span className={r.is_architect ? "text-amber-300" : "text-zinc-400"}>{r.author_name}</span>
          <span className="text-zinc-200 flex-1 whitespace-pre-wrap">{r.body}</span>
          {r.can_delete && (
            <button onClick={() => remove(r.reply_id)} data-testid={`advisory-reply-delete-${r.reply_id}`}
              className="text-zinc-600 hover:text-red-400 transition-colors" aria-label="Delete reply">
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <input value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000}
          placeholder="Reply or ask a question…" data-testid={`advisory-reply-input-${id}`}
          onKeyDown={(e) => e.key === "Enter" && send()}
          className="flex-1 bg-black border border-zinc-800 px-3 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500/50" />
        <button onClick={send} disabled={busy || !body.trim()} data-testid={`advisory-reply-send-${id}`}
          className="px-3 border border-zinc-700 text-zinc-300 hover:border-amber-500/60 hover:text-amber-300 disabled:opacity-40 transition-colors">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}

function AdvisoryForm({ initial, submitLabel, onSubmit, onCancel, testId }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const input = "w-full bg-black border border-zinc-800 px-3 py-2 text-xs text-zinc-100 focus:outline-none focus:border-amber-500/50";

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(f);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-dashed border-zinc-700 p-4 space-y-2" data-testid={testId}>
      <input className={input} placeholder="Title" value={f.title} onChange={set("title")} data-testid={`${testId}-title`} />
      <div className="flex gap-2">
        <Picker value={f.severity} onChange={set("severity")} options={Object.keys(SEVERITY)} testId={`${testId}-severity`} />
        <Picker value={f.status} onChange={set("status")} options={STATUSES} testId={`${testId}-status`} />
      </div>
      <textarea className={input} rows={3} placeholder="What happened (plain language, no attack details)"
        value={f.summary} onChange={set("summary")} data-testid={`${testId}-summary`} />
      <textarea className={input} rows={2} placeholder="Solution" value={f.solution}
        onChange={set("solution")} data-testid={`${testId}-solution`} />
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy || f.title.length < 3 || f.summary.length < 3} data-testid={`${testId}-submit`}
          className="px-4 py-2 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 text-[11px] uppercase tracking-[0.25em] disabled:opacity-40 transition-colors">
          {busy ? "Saving…" : submitLabel}
        </button>
        {onCancel && (
          <button onClick={onCancel} data-testid={`${testId}-cancel`}
            className="px-4 py-2 text-zinc-500 hover:text-zinc-200 text-[11px] uppercase tracking-[0.25em] transition-colors">Cancel</button>
        )}
      </div>
    </div>
  );
}

function AdvisoryCard({ adv, canManage, onChange, onDelete }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const id = adv.advisory_id;
  const count = (adv.replies || []).length;

  const patch = async (fields) => {
    const { data } = await client.patch(`/security/advisories/${id}`, fields);
    onChange({ ...data.advisory, replies: adv.replies });
  };

  const share = async () => {
    const url = `${window.location.origin}/advisory/${id}`;
    try {
      if (navigator.share) await navigator.share({ title: adv.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.message(url);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this advisory?")) return;
    try {
      await client.delete(`/security/advisories/${id}`);
      onDelete(id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (editing) {
    const { title, severity, status, summary, solution } = adv;
    return (
      <AdvisoryForm initial={{ title, severity, status, summary, solution }} submitLabel="Save"
        testId={`advisory-edit-form-${id}`} onCancel={() => setEditing(false)}
        onSubmit={async (f) => { await patch(f); setEditing(false); toast.success("Advisory updated"); }} />
    );
  }

  return (
    <article className={`border-l-2 ${SEVERITY[adv.severity]} bg-[#0b0b0d] border border-zinc-800 p-4`}
      data-testid={`advisory-card-${id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className={`text-[10px] uppercase tracking-[0.25em] ${SEVERITY[adv.severity].split(" ")[1]}`}
          data-testid={`advisory-severity-${id}`}>{adv.severity}</span>
        {canManage ? (
          <Picker value={adv.status} options={STATUSES} testId={`advisory-status-select-${id}`}
            className={`w-36 ${STATUS[adv.status]}`}
            onChange={(status) => patch({ status }).catch((e) => toast.error(errMsg(e)))} />
        ) : (
          <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${STATUS[adv.status]}`}
            data-testid={`advisory-status-${id}`}>{adv.status}</span>
        )}
        <span className="text-[10px] text-zinc-600 ml-auto">{new Date(adv.created_at).toLocaleDateString()}</span>
        {adv.hidden && (
          <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 bg-zinc-800 text-zinc-300 inline-flex items-center gap-1"
            data-testid={`advisory-hidden-badge-${id}`}><EyeOff className="h-3 w-3" /> hidden from others</span>
        )}
        {adv.status === "fixed" && !adv.hidden && (
          <button onClick={share} data-testid={`advisory-share-${id}`} aria-label="Share public link"
            className="p-1.5 text-zinc-500 hover:text-emerald-300 transition-colors"><Share2 className="h-3 w-3" /></button>
        )}
        {canManage && (
          <>
            <button onClick={() => patch({ hidden: !adv.hidden }).then(() => toast.success(adv.hidden ? "Visible to everyone again" : "Hidden from everyone but you")).catch((e) => toast.error(errMsg(e)))}
              data-testid={`advisory-visibility-${id}`} aria-label={adv.hidden ? "Show to everyone" : "Hide from others"} title={adv.hidden ? "Show to everyone" : "Hide from others"}
              className="p-1.5 text-zinc-500 hover:text-sky-300 transition-colors">
              {adv.hidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            </button>
            <button onClick={() => setEditing(true)} data-testid={`advisory-edit-${id}`} aria-label="Edit advisory"
              className="p-1.5 text-zinc-500 hover:text-amber-300 transition-colors"><Pencil className="h-3 w-3" /></button>
            <button onClick={remove} data-testid={`advisory-delete-${id}`} aria-label="Delete advisory"
              className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="h-3 w-3" /></button>
          </>
        )}
      </div>
      <h3 className="text-sm text-zinc-100 font-medium" data-testid={`advisory-title-${id}`}>{adv.title}</h3>
      <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{adv.summary}</p>
      {adv.solution && (
        <p className="text-xs text-emerald-200/80 mt-2 leading-relaxed" data-testid={`advisory-solution-${id}`}>
          <span className="uppercase tracking-[0.2em] text-[10px] text-emerald-400 mr-2">Solution</span>{adv.solution}
        </p>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid={`advisory-toggle-replies-${id}`}
        className="mt-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1.5 transition-colors">
        <MessageSquare className="h-3 w-3" /> {count} {count === 1 ? "reply" : "replies"}
      </button>
      {open && <Replies adv={adv} onChange={onChange} />}
    </article>
  );
}

const EMPTY = { title: "", severity: "info", status: "open", summary: "", solution: "" };

export const SecurityAdvisories = ({ signedIn }) => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setError(null);
    client.get("/security/advisories")
      .then(({ data }) => { setItems(data.advisories); setCanManage(data.can_manage); })
      .catch((e) => setError(errMsg(e)));
  };

  useEffect(() => {
    if (signedIn) load();
  }, [signedIn]);

  const replace = (a) => setItems((xs) => xs.map((x) => (x.advisory_id === a.advisory_id ? a : x)));
  const create = async (f) => {
    const { data } = await client.post("/security/advisories", f);
    setItems((xs) => [data.advisory, ...xs]);
    setShowForm(false);
    toast.success("Advisory posted");
  };

  let body;
  if (!signedIn) body = <p className="text-xs text-zinc-500" data-testid="advisories-signin-required">Sign in to read security advisories.</p>;
  else if (error) body = (
    <div className="text-xs text-red-300 flex items-center gap-3" data-testid="advisories-error">
      Couldn&apos;t load advisories: {error}
      <button onClick={load} data-testid="advisories-retry" className="underline text-zinc-300 hover:text-zinc-100">Retry</button>
    </div>
  );
  else if (items === null) body = <Loader2 className="h-4 w-4 animate-spin text-zinc-500" data-testid="advisories-loading" />;
  else body = (
    <div className="space-y-3" data-testid="advisories-list">
      {showForm && <AdvisoryForm initial={EMPTY} submitLabel="Post advisory" testId="advisory-new-form"
        onSubmit={create} onCancel={() => setShowForm(false)} />}
      {items.length === 0 && <p className="text-xs text-zinc-500" data-testid="advisories-empty">No advisories yet.</p>}
      {items.map((a) => (
        <AdvisoryCard key={a.advisory_id} adv={a} canManage={canManage} onChange={replace}
          onDelete={(id) => setItems((xs) => xs.filter((x) => x.advisory_id !== id))} />
      ))}
    </div>
  );

  return (
    <section data-testid="settings-security-advisories" className="mt-16 border-t border-zinc-800 pt-10">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-400 mb-1 inline-flex items-center gap-2">
            <ShieldAlert className="h-3 w-3" /> security advisories
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-[0.1em] text-zinc-100">What we found &amp; fixed</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
            An open log of security findings and how each one was solved. Ask questions in the replies.
          </p>
        </div>
        {canManage && (
          <button onClick={() => setShowForm((s) => !s)} data-testid="advisory-new-toggle"
            className="shrink-0 px-3 py-2 border border-zinc-700 text-zinc-300 hover:border-amber-500/60 hover:text-amber-300 text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-1.5 transition-colors">
            <Plus className="h-3 w-3" /> New
          </button>
        )}
      </div>
      {body}
    </section>
  );
};
